import { Platform } from '@angular/cdk/platform';
import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  EventEmitter,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface FullscreenHtmlElement extends HTMLElement {
  webkitRequestFullscreen: () => Promise<void>;
}

export interface FullscreenDocument extends Document {
  webkitCancelFullScreen: () => Promise<void>;
}

@Directive({
  selector: '[atxFullscreen]',
  exportAs: 'atxFullscreen',
  standalone: false,
})
export class FullscreenDirective implements OnInit, OnDestroy {
  @Output()
  public fullscreenChanged = new EventEmitter<boolean>();
  public destroy$ = new Subject<void>();
  public opened = false;

  constructor(
    @Inject(DOCUMENT) private document: FullscreenDocument,
    private host: ElementRef<FullscreenHtmlElement>,
    private renderer: Renderer2,
    private platform: Platform,
  ) {}

  public ngOnInit() {
    this.renderer.setAttribute(
      this.host.nativeElement,
      'fullscreenelement',
      '',
    );
    this.watchFullScreenChange();
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async open() {
    this.platform.SAFARI
      ? await this.host.nativeElement.webkitRequestFullscreen()
      : await this.host.nativeElement.requestFullscreen();
  }

  public async close() {
    this.platform.SAFARI
      ? await this.document.webkitCancelFullScreen()
      : await this.document.exitFullscreen();
  }

  private setOpenedState(value: boolean) {
    this.opened = value;
  }

  /**
   * Function to detect if the user close the fullscreen
   * using the keyboard instead of programatically
   * @private
   * @memberof FullscreenDirective
   */
  private watchFullScreenChange() {
    const eventName = this.platform.SAFARI
      ? 'webkitfullscreenchange'
      : 'fullscreenchange';

    fromEvent(this.host.nativeElement, eventName)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setOpenedState(!this.opened);
        this.fullscreenChanged.emit(this.opened);
      });
  }
}
